import { z } from "zod";

/**
 * How one EUI compares between this admin and The Things Stack (ADR-089).
 *
 * IN_SYNC: linked and equal. DRIFT: linked, but a field differs.
 * UNLINKED: the same EUI on both sides, but the row here is not tied to the
 * Network Server entry (typed into the console by hand, or under another id),
 * so nothing is pushed to it until it is linked. ONLY_IN_ADMIN and
 * ONLY_IN_NETWORK: one side has never heard of it.
 */
export const RECONCILIATION_STATUSES = [
  "IN_SYNC",
  "DRIFT",
  "UNLINKED",
  "ONLY_IN_ADMIN",
  "ONLY_IN_NETWORK",
] as const;
export const reconciliationStatusSchema = z.enum(RECONCILIATION_STATUSES);
export type ReconciliationStatus = z.infer<typeof reconciliationStatusSchema>;

/**
 * Fields compared. Device: name, model (Device Repository ids), frequency
 * plan, LoRaWAN MAC and PHY version, class C, payload formatter, and whether
 * the Network or Application Server holds the device at all. Gateway: name
 * and frequency plan.
 */
export const RECONCILIATION_FIELDS = [
  "name",
  "model",
  "frequencyPlan",
  "macVersion",
  "phyVersion",
  "classC",
  "formatter",
  "networkServer",
  "applicationServer",
] as const;
export const reconciliationFieldSchema = z.enum(RECONCILIATION_FIELDS);
export type ReconciliationField = z.infer<typeof reconciliationFieldSchema>;

export const fieldDifferenceSchema = z.object({
  field: reconciliationFieldSchema,
  /** What this admin says it should be; null when it has no opinion. */
  admin: z.string().nullable(),
  network: z.string().nullable(),
});
export type FieldDifference = z.infer<typeof fieldDifferenceSchema>;

/**
 * What can be done from the reconciliation view. LINK ties a row to the
 * entry with the same EUI; PUSH overwrites the Network Server with this
 * admin's values; REGISTER creates a gateway known only here; IMPORT creates
 * a row here for a gateway known only there. Devices are never registered or
 * imported from here: registering needs the AppKey, and an import would have
 * to guess the organization and model.
 */
export const RECONCILIATION_ACTIONS = ["LINK", "PUSH", "REGISTER", "IMPORT"] as const;
export const reconciliationActionSchema = z.enum(RECONCILIATION_ACTIONS);
export type ReconciliationAction = z.infer<typeof reconciliationActionSchema>;

/**
 * Why no action is offered. NEEDS_APP_KEY: only here, and registering a
 * device needs its AppKey. OTHER_APPLICATION: recorded under an application
 * that is not compared. NOT_IMPORTED: devices are never imported.
 * UNSUPPORTED_MODEL: no catalogue profile to push. NO_FREQUENCY_PLAN: the row
 * has none to push. MISSING_REGISTRY: the entry is absent from the Network or
 * Application Server and must be registered again. FOREIGN_NETWORK_ID: the
 * row names a network id this admin did not register. NO_EUI: the entry has
 * no EUI to match or import by.
 */
export const RECONCILIATION_BLOCKERS = [
  "NEEDS_APP_KEY",
  "OTHER_APPLICATION",
  "NOT_IMPORTED",
  "UNSUPPORTED_MODEL",
  "NO_FREQUENCY_PLAN",
  "MISSING_REGISTRY",
  "FOREIGN_NETWORK_ID",
  "NO_EUI",
] as const;
export const reconciliationBlockerSchema = z.enum(RECONCILIATION_BLOCKERS);
export type ReconciliationBlocker = z.infer<typeof reconciliationBlockerSchema>;

const itemBase = {
  status: reconciliationStatusSchema,
  differences: z.array(fieldDifferenceSchema),
  /** Null when nothing can be done here; `blockedBy` may say why. */
  action: reconciliationActionSchema.nullable(),
  blockedBy: reconciliationBlockerSchema.nullable(),
  organizationName: z.string().nullable(),
};

export const deviceReconciliationSchema = z.object({
  ...itemBase,
  devEui: z.string(),
  deviceId: z.uuid().nullable(),
  model: z.string().nullable(),
  networkDeviceId: z.string().nullable(),
  networkName: z.string().nullable(),
});
export type DeviceReconciliation = z.infer<typeof deviceReconciliationSchema>;

export const gatewayReconciliationSchema = z.object({
  ...itemBase,
  eui: z.string(),
  gatewayId: z.uuid().nullable(),
  name: z.string().nullable(),
  networkGatewayId: z.string().nullable(),
  networkName: z.string().nullable(),
});
export type GatewayReconciliation = z.infer<typeof gatewayReconciliationSchema>;

export const reconciliationReportSchema = z.object({
  checkedAt: z.iso.datetime(),
  applicationId: z.string(),
  organizationId: z.string(),
  devices: z.array(deviceReconciliationSchema),
  gateways: z.array(gatewayReconciliationSchema),
});
export type ReconciliationReport = z.infer<typeof reconciliationReportSchema>;

/** LINK names the entry explicitly, so a stale report cannot link the wrong one. */
export const linkDeviceRequestSchema = z.object({ networkDeviceId: z.string().trim().min(1) });
export type LinkDeviceRequest = z.infer<typeof linkDeviceRequestSchema>;

export const linkGatewayRequestSchema = z.object({ networkGatewayId: z.string().trim().min(1) });
export type LinkGatewayRequest = z.infer<typeof linkGatewayRequestSchema>;

export const importGatewayRequestSchema = linkGatewayRequestSchema;
export type ImportGatewayRequest = LinkGatewayRequest;
